import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../context/AuthContext';
import { useAccount } from '../context/AccountContext';
import DesktopLayout from '../layouts/DesktopLayout';
import GuestCard from '../components/GuestCard';
import { useGuestsPaged, GUESTS_QUERY_KEY } from '../hooks/useGuests';
import useLanguage from '../hooks/useLanguage';
import { getGuestSourceLabel, getGuestTier } from '../utils/guestDisplay';
import GuestFilters from '../components/GuestFilters';
import AddGuestModal from '../components/AddGuestModal';
import EditGuestModal from '../components/EditGuestModal';
import AccountSwitcher from '../components/common/AccountSwitcher';
import { roleConfig, CORE_STAFF_ROLES, isOwnerRole } from '../config/roleConfig';
import { formatDate } from '../utils/dateFormatter';
import '../styles/GuestsPage.css';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_STATS = { total: 0, new: 0, repeat: 0 };

const GuestsPage = () => {
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useAccount(); // Global account selection from Context
  const navigate = useNavigate();

  const { t } = useLanguage();
  const queryClient = useQueryClient();
  // Owners get the phone card view + collapsible filters (tasks/owner-screens-mobile.md D1/D2).
  const isOwner = isOwnerRole(user?.role);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    source: '',
    minBookings: '',
    sortBy: 'lastBookingDate',
    sortOrder: -1
  });

  // Search hits the server, so wait for the typing to pause.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Rows load a page at a time; totals and counts come from the server over the whole set.
  const {
    guests,
    total,
    stats: serverStats,
    isLoading: loading,
    error: queryError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError
  } = useGuestsPaged({
    search: debouncedSearch || undefined,
    source: filters.source || undefined,
    minBookings: filters.minBookings || undefined,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    ownerId: selectedAccount || undefined
  }, PAGE_SIZE);
  const stats = serverStats || EMPTY_STATS;
  const error = queryError ? 'حدث خطأ في جلب الضيوف' : null;
  const activeFilterCount = [filters.search.trim(), filters.source, filters.minBookings].filter(Boolean).length;

  // Navigation state
  const [activeNavItem, setActiveNavItem] = useState('guests');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const handleGuestClick = (guestId) => {
    navigate(`/guests/${guestId}`);
  };

  const handleAddGuest = () => {
    setShowAddModal(true);
  };

  const handleEditGuest = (guest) => {
    setSelectedGuest(guest);
    setShowEditModal(true);
  };

  const refreshGuests = () => queryClient.invalidateQueries({ queryKey: [GUESTS_QUERY_KEY] });
  const handleGuestAdded = refreshGuests;
  const handleGuestUpdated = refreshGuests;

  const isEditableSource = (source) => {
    const editableSources = ['direct', 'manual'];
    return editableSources.includes(source);
  };

  const canManageGuests = () => {
    return user && roleConfig[user.role]?.capabilities?.manageGuests;
  };

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="إدارة الضيوف"
    >
      <div className={`guests-page${isOwner ? ' owner-view' : ''}${filtersOpen ? ' filters-open' : ''}`}>
        {/* Account Switcher - For admin/ops only */}
        {CORE_STAFF_ROLES.includes(user?.role) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <AccountSwitcher />
          </div>
        )}

        {/* Stats Cards — only once the server has answered: 0/0/0 while loading or after a failed
            read would read as "no guests" (tasks/owner-screens-mobile.md step 7) */}
        {serverStats && (
        <div className="guest-stats">
          <div className="stat-card touch-card">
            <div className="stat-icon">
              <i className="bi bi-people"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">إجمالي الضيوف</div>
            </div>
          </div>

          <div className="stat-card touch-card">
            <div className="stat-icon new">
              <i className="bi bi-person-plus"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.new}</div>
              <div className="stat-label">ضيوف جدد</div>
            </div>
          </div>

          <div className="stat-card touch-card">
            <div className="stat-icon repeat">
              <i className="bi bi-arrow-repeat"></i>
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.repeat}</div>
              <div className="stat-label">ضيوف متكررين</div>
            </div>
          </div>
        </div>
        )}

        {/* Phone (owners): filters fold behind one button so the list starts on screen one */}
        <button
          type="button"
          className="btn btn-outline-primary touch-btn guests-filters-toggle"
          onClick={() => setFiltersOpen(open => !open)}
          aria-expanded={filtersOpen}
        >
          <i className="bi bi-funnel"></i> {t('lists.filtersToggle')}
          {activeFilterCount > 0 && <span className="filters-toggle-count">{activeFilterCount}</span>}
        </button>

        {/* Filters and Actions Bar */}
        <div className="filters-actions-bar">
          <GuestFilters
            filters={filters}
            onFilterChange={handleFilterChange}
          />
          {canManageGuests() && (
            <button className="btn-add-guest" onClick={handleAddGuest}>
              <i className="bi bi-person-plus"></i>
              إضافة ضيف جديد
            </button>
          )}
        </div>

        {/* Guests List */}
        <div className="guests-container">
          {loading && guests.length === 0 ? (
            <div className="loading-state">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          ) : error && guests.length === 0 ? (
            <div className="error-state">
              <i className="bi bi-exclamation-circle"></i>
              <p>{error}</p>
              <button type="button" className="btn btn-outline-primary touch-btn" onClick={() => refetch()}>
                {t('states.retry')}
              </button>
            </div>
          ) : guests.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-people"></i>
              <p>{activeFilterCount > 0 ? t('guestsList.noMatches') : t('guestsList.noGuests')}</p>
            </div>
          ) : (
            <>
              <div className="guests-count-row">
                <span className="guests-count">
                  {t('lists.showingOf').replace('{shown}', guests.length).replace('{total}', total)}
                </span>
              </div>

              {/* Guests Table */}
              <div className="guests-table-container">
                <table className="guests-table">
                  <thead>
                    <tr>
                      <th>الضيف</th>
                      <th>البريد الإلكتروني</th>
                      <th>رقم الهاتف</th>
                      <th>الحجوزات</th>
                      <th>آخر حجز</th>
                      <th>المصدر</th>
                      <th>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guests.map(guest => {
                      const tier = getGuestTier(guest.totalBookings || 0);
                      return (
                        <tr key={guest._id} className="guest-row">
                          <td>
                            <div className="guest-cell">
                              <div className="guest-avatar-sm">
                                {guest.name?.charAt(0).toUpperCase() || 'G'}
                              </div>
                              <div className="guest-info-sm">
                                <div className="guest-name-sm">{guest.name || t('guestsList.unnamed')}</div>
                                <span className={`tier-badge-sm ${tier.color}`}>
                                  <i className={`bi ${tier.icon}`}></i> {tier.label}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="text-muted">
                              {guest.email ? (
                                <>
                                  <i className="bi bi-envelope me-1"></i>
                                  <span style={{ direction: 'ltr', display: 'inline-block' }}>{guest.email}</span>
                                </>
                              ) : '-'}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted">
                              {guest.phone ? (
                                <>
                                  <i className="bi bi-telephone me-1"></i>
                                  <span style={{ direction: 'ltr', display: 'inline-block' }}>{guest.phone}</span>
                                </>
                              ) : '-'}
                            </span>
                          </td>
                          <td>
                            <span className="badge-bookings">
                              <i className="bi bi-calendar3"></i> {guest.totalBookings || 0}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted">
                              {formatDate(guest.lastBookingDate)}
                            </span>
                          </td>
                          <td>
                            <span className="source-badge-sm">
                              {getGuestSourceLabel(guest.acquisitionSource)}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              {canManageGuests() && isEditableSource(guest.acquisitionSource) && (
                                <button
                                  className="btn-edit-sm touch-icon-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditGuest(guest);
                                  }}
                                  title="تعديل البيانات"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              )}
                              <button
                                className="btn-view-sm touch-icon-btn"
                                onClick={() => handleGuestClick(guest._id)}
                                title="عرض التفاصيل"
                              >
                                <i className="bi bi-eye"></i> عرض
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Phone card view — owners only; CSS shows it below 768px instead of the table */}
              {isOwner && (
                <div className="guests-cards">
                  {guests.map(guest => <GuestCard key={guest._id} guest={guest} />)}
                </div>
              )}

              {hasNextPage && (
                <div className="list-load-more">
                  <button
                    type="button"
                    className="btn btn-outline-primary touch-btn"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? t('lists.loadingMore') : t('lists.loadMore')}
                  </button>
                  {isFetchNextPageError && !isFetchingNextPage && (
                    <span className="text-danger" role="alert" style={{ marginInlineStart: '12px' }}>
                      {t('lists.loadMoreFailed')}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Modals */}
        <AddGuestModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onGuestAdded={handleGuestAdded}
        />

        <EditGuestModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          guest={selectedGuest}
          onGuestUpdated={handleGuestUpdated}
        />
      </div>
    </DesktopLayout>
  );
};

export default GuestsPage;
